import React, { useEffect, useContext, useMemo } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { Role, User } from '../../dto';
import { useLoggedInUser, useUpdateUserPreferences, useUserPreferences } from '../../hooks';
import StudentProvider from '../StudentProvider';
import { ApplicationInsightsContext } from '../ApplicationInsightsProvider';
import { CircularProgress, Grid } from '@material-ui/core';
import { LocalStorageKey } from '../../hooks/useLocalStorage';
import { PreferenceKey } from 'tuapath-common/generated/schema';

interface UserContextProps {
  user: User;
}

export const UserContext = React.createContext<UserContextProps>({
  get user(): User {
    return { id: -1, roles: [Role.STUDENT]} as User;
  }
});

const AuthRedirect: React.FC = () => {
  const location = useLocation();
  return <Redirect to={{ pathname: '/login', state: { from: location } }} />;
};

export const UserProvider: React.FC = ({ children }) => {
  const insightsCtx = useContext(ApplicationInsightsContext);
  const { loading, data } = useLoggedInUser({ fetchPolicy: 'cache-first', errorPolicy: 'ignore' });
  const [updateUserPreferences] = useUpdateUserPreferences();

  const allPreferenceKeys = useMemo(() => {
    return Object.values(LocalStorageKey).filter(key =>
      Object.values(PreferenceKey).includes(key as unknown as PreferenceKey)
    ).map(key => key as unknown as PreferenceKey);
  }, []);

  const existingUserPreferences = useUserPreferences({
    variables: {
      userId: data?.me?.id ?? 0,
      keys: allPreferenceKeys
    },
    skip: !data?.me?.id || allPreferenceKeys.length === 0
  });

  const storedUserEntries = useMemo(() => {
    const parse = (s: string | null) => {
      try { return s ? JSON.parse(s) : null; } catch { return s; }
    };
    return (Object.values(LocalStorageKey) as string[])
      .map(key => ({ key, value: parse(window.localStorage.getItem(key)) }))
      .filter(entry => entry.value !== null && entry.value !== undefined);
  }, []);

  useEffect(() => {
    const migratePreferences = async () => {
      if (data?.me?.id &&
          existingUserPreferences.data?.userPreference &&
          storedUserEntries.length > 0) {

        try {
          const existingKeys = new Set(
            existingUserPreferences.data.userPreference.map(pref => pref.key)
          );

          const preferencesToMigrate = storedUserEntries
            .filter(entry =>
              Object.values(PreferenceKey).includes(entry.key as PreferenceKey) &&
              !existingKeys.has(entry.key as PreferenceKey)
            )
            .map(entry => ({
              key: entry.key as PreferenceKey,
              value: JSON.stringify(entry.value)
            }));

          if (preferencesToMigrate.length > 0) {
            await updateUserPreferences({
              variables: {
                input: preferencesToMigrate
              }
            });

            console.log(`Migrated ${preferencesToMigrate.length} user preferences from localStorage`);
          }
        } catch (error) {
          console.error('Failed to migrate user preferences:', error);
        }
      }
    };

    void migratePreferences();
  }, [data?.me?.id, existingUserPreferences.data, storedUserEntries, updateUserPreferences]);

  useEffect(() => {
    if (data?.me?.telemetryId && insightsCtx.telemetryId != data.me.telemetryId) {
      insightsCtx.setTelemetryId(data.me.telemetryId);
      insightsCtx.setUsername(data.me.username);
    }
  }, [data?.me]);

  return loading ? (
    <Grid container justify="center" alignItems="center" style={{ height: '100vh' }}>
      <Grid item>
        <CircularProgress color="primary" />
      </Grid>
    </Grid>
  ) : data?.me ? (
    <UserContext.Provider value={{ user: data.me }}>
      <StudentProvider>{children}</StudentProvider>
    </UserContext.Provider>
  ) : (
    <AuthRedirect />
  );
};

export default UserProvider;
